import { IconWarningRegular, skinVars, Stack, Text3, useDialog } from '@telefonica/mistica';
import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EProblemErrorMessage, EProblemFeedback, EProblemFeedbackType } from '../../models/Problems.interfaces';
import { literalsFeedback } from '../../pages/support/utils.chat';
import ApisClient, { ApisClientNoLoader } from '../client/ApisClient';
import ProblemsClient from '../client/ProblemsClient';
import { handleErrorMessage, setInfoError } from '../dataTransformation/errorMesssages';
import navigateParams from '../navigation/navigateParams';
import { ETypeUser, IErrorGlobal } from '../store/model/store.model';
import { v4 as uuidv4 } from 'uuid';
import TokenHandlerClient from '../client/TokenHandlerClient';
import { ErrorStateUIData } from '../../models/interfaces';
import { managementEnvironment } from '../dataTransformation/environments';

const GlobalError = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const personalDataDisabled = managementEnvironment().REACT_APP_STOPPER_PROFILE_CUSTOMER_DATA_DISABLED;
    const homeDisabled = managementEnvironment().REACT_APP_STOPPER_HOME_DISABLED;
    let isUnauthorized = useRef<boolean>(false);
    const clientType = new URLSearchParams(window.location.search).get('clientType');
    const { dialog } = useDialog();

    const handleManagementSession = (error: AxiosError, cb: CallableFunction) => {
        if (Object.keys(error.response?.headers || {}).includes('x-r-event')) {
            const queryParams = { data: error.response?.headers['x-r-event'] };
            navigateParams('navigation-refresh-cookie', '', queryParams);
        }
        cb(error);
    };

    const handleErrorHttp = (error: AxiosError) => {
        let _error: IErrorGlobal = {
            ...handleErrorMessage(error),
        };
        if (clientType === ETypeUser.PRECLIENT) {
            handleRedirectPR(error, _error);
        } else {
            handleRedirectNormal(error, _error);
        }
    };

    const handleRedirectPR = (error: AxiosError, literals: IErrorGlobal) => {
        const pathsPR = ['products'];
        const pathsNotRedirectPR = pathsPR.some((path: string) => (error.config?.url as string)?.includes(path));
        if (!pathsNotRedirectPR) {
            navigate(`/error?clientType=${new URLSearchParams(window.location.search).get('clientType')}`, {
                state: literals,
            });
        }
    };

    const handleRedirectNormal = (error: AxiosError, literals: IErrorGlobal) => {
        const paths = [
            '/support',
            '/products/datasharing/hire',
            '/products/datasharing/cancel',
            '/customer/data/update',
            '/catalog/document',
            '/customer/accounts/update',
            '/escalation/otp/send',
            '/customer/contact/verify/otp/validate',
            '/products/puk',
            '/customer/password/update',
            '/products/devices',
            '/sims/infos',
            '/sims/physical/duplicate',
            '/sims/esim/duplicate',
            '/bills/download',
        ];
        const pathsNotRedirect = paths.some((path: string) => (error.config?.url as string)?.includes(path));
        if (!personalDataDisabled || !homeDisabled) {
            if (!pathsNotRedirect) {
                const url = new URLSearchParams(window.location.search).has('clientType')
                    ? `/error?clientType=${new URLSearchParams(window.location.search).get('clientType')}`
                    : '/error';

                navigate(url, { state: literals });
            }
        }
    };

    const notValidSession = (error: ErrorStateUIData) => {
        const { id, localizedDescription, title, httpCode } = error;
        isUnauthorized.current = true;
        if (navigateParams('navigation-native-critical-error', '', { id, localizedDescription, httpCode }, '', '')) {
            dialog({
                icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={53} />,
                title,
                message: (() => {
                    return (
                        <Stack space={16}>
                            <Text3 light color={skinVars.colors.textSecondary}>
                                {localizedDescription}
                            </Text3>
                            {id && (
                                <Text3 light color={skinVars.colors.textSecondary}>
                                    {`${t('codeError')} ${id}`}
                                </Text3>
                            )}
                        </Stack>
                    );
                })() as any,
                acceptText: t('buttonUnderstoodFeedback'),
                onAccept: () => (window.location.href = '/logout'),
                onCancel: () => (window.location.href = '/logout'),
            });
        }
    };

    const handleErrorBeApis = (error: AxiosError) => {
        let _error: IErrorGlobal = {
            ...handleErrorMessage(error),
        };
        const { id } = setInfoError(error);
        window.localStorage.setItem('breadcrumb', JSON.stringify([{ url: window.location.href }]));
        localStorage.setItem('error', JSON.stringify(setInfoError(error)));
        const controlSessionNotValid: boolean = [401, 403, 410].includes(Number(error.response?.status));
        const controlLOA: boolean = ['X401502'].includes(id as string);
        if (controlSessionNotValid) {
            if (controlLOA) {
                handleRedirectNormal(error, _error);
            } else {
                notValidSession(setInfoError(error));
            }
        } else {
            handleManagementSession(error, handleErrorHttp);
        }
    };

    const handleErrorBeProblems = (error: AxiosError) => {
        const { localizedDescription } = setInfoError(error);

        if (localizedDescription === EProblemErrorMessage.ERROR_MASIVE) {
            const queryParams = {
                phase: EProblemFeedbackType.ERROR,
                error: EProblemFeedback.ERROR_MASIVE,
                ...literalsFeedback(EProblemFeedback.ERROR_MASIVE),
            };
            if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
            }
        }
    };

    useEffect(() => {
        const reqInterceptorClient = ApisClient.interceptors.request.use((config) => {
            config.headers!['X-Request-Id'] = uuidv4();
            return config;
        });
        const resInterceptorClient = ApisClient.interceptors.response.use(
            (response: AxiosResponse) => {
                if (Object.keys(response.headers || {}).includes('x-r-event')) {
                    const queryParams = { data: response.headers['x-r-event'] };
                    navigateParams('navigation-refresh-cookie', '', queryParams);
                }
                return response;
            },
            (error: AxiosError) => {
                handleErrorBeApis(error);
                return Promise.reject(error);
            },
        );

        const reqInterceptorClientLoader = ApisClientNoLoader.interceptors.request.use((config) => {
            return config;
        });

        const resInterceptorClientLoader = ApisClientNoLoader.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            (error: AxiosError) => {
                handleErrorBeApis(error);
                return Promise.reject(error);
            },
        );
        const reqInterceptorProblems = ProblemsClient.interceptors.request.use((config) => {
            config.headers!['X-Request-Id'] = uuidv4();
            return config;
        });
        const resInterceptorProblems = ProblemsClient.interceptors.response.use(
            (response: AxiosResponse) => {
                if (Object.keys(response.headers || {}).includes('x-r-event')) {
                    const queryParams = { data: response.headers['x-r-event'] };
                    navigateParams('navigation-refresh-cookie', '', queryParams);
                }
                return response;
            },
            (error: AxiosError) => {
                /* console.error('INDEX be/problems', error); */
                sessionStorage.setItem('error', JSON.stringify(setInfoError(error)));
                const { id, localizedDescription, title, httpCode } = setInfoError(error);
                if ([401, 403, 410].includes(Number(error.response?.status))) {
                    if (
                        navigateParams(
                            'navigation-native-critical-error',
                            '',
                            { id, localizedDescription, httpCode },
                            '',
                            '',
                        )
                    ) {
                        dialog({
                            icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={53} />,
                            title,
                            message: (() => {
                                return (
                                    <Stack space={16}>
                                        <Text3 light color={skinVars.colors.textSecondary}>
                                            {localizedDescription}
                                        </Text3>
                                        {id && (
                                            <Text3 light color={skinVars.colors.textSecondary}>
                                                {`${t('codeError')} ${id}`}
                                            </Text3>
                                        )}
                                    </Stack>
                                );
                            })() as any,
                            acceptText: t('buttonUnderstoodFeedback'),
                            onAccept: () => navigate('/login'),
                            onCancel: () => navigate('/login'),
                        });
                    }
                } else {
                    handleManagementSession(error, handleErrorBeProblems);
                    // if (localizedDescription === EProblemErrorMessage.ERROR_MASIVE) {
                    //     const queryParams = {
                    //         phase: EProblemFeedbackType.ERROR,
                    //         error: EProblemFeedback.ERROR_MASIVE,
                    //         ...literalsFeedback(EProblemFeedback.ERROR_MASIVE),
                    //     };
                    //     if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                    //         navigate(
                    //             `/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`,
                    //         );
                    //     }
                    // }
                }
                return Promise.reject(error);
            },
        );

        const reqInterceptorTokenHandler = TokenHandlerClient.interceptors.request.use((config) => {
            config.headers!['X-Request-Id'] = uuidv4();
            return config;
        });

        const resInterceptorTokenHandler = TokenHandlerClient.interceptors.response.use(
            (response: AxiosResponse) => {
                if (Object.keys(response.headers || {}).includes('x-r-event')) {
                    const queryParams = { data: response.headers['x-r-event'] };
                    navigateParams('navigation-refresh-cookie', '', queryParams);
                }
                return response;
            },
            (error: AxiosError) => {
                console.error('INDEX be/th', error);
                handleErrorBeApis(error);
                return Promise.reject(error);
            },
        );

        return () => {
            ApisClient.interceptors.request.eject(reqInterceptorClient);
            ApisClient.interceptors.response.eject(resInterceptorClient);
            ApisClientNoLoader.interceptors.request.eject(reqInterceptorClientLoader);
            ApisClientNoLoader.interceptors.response.eject(resInterceptorClientLoader);
            ProblemsClient.interceptors.request.eject(reqInterceptorProblems);
            ProblemsClient.interceptors.response.eject(resInterceptorProblems);
            TokenHandlerClient.interceptors.request.eject(reqInterceptorTokenHandler);
            TokenHandlerClient.interceptors.response.eject(resInterceptorTokenHandler);
        };
    }, []);

    return <>{isUnauthorized.current && <div className='c-loggeding'></div>}</>;
};

export default GlobalError;
